import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { ShoppingCart, ExternalLink, Leaf } from "lucide-react";
import { productImg } from "@/lib/assets";
import { trackEvent } from "@/lib/analytics";
import { useRegion } from "@/context/RegionContext";
import { formatPrice } from "@/lib/regions";

export default function Store() {
  const [products, setProducts] = useState([]);
  const [loadingId, setLoadingId] = useState(null);
  const { region } = useRegion();
  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)); }, []);

  const buy = async (p) => {
    if (p.type === "referral") { trackEvent("share_click", { kind: "referral", store: p.store }); window.open(p.referral_url, "_blank"); return; }
    setLoadingId(p.id);
    try {
      const { data } = await api.post("/checkout/session", { product_id: p.id, origin_url: window.location.origin });
      trackEvent("checkout_start", { product: p.name });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoadingId(null);
    }
  };

  return (
    <main className="bg-onyx min-h-screen pt-16" data-testid="store-page">
      <div className="border-b border-white/10 grain bg-surface">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16">
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4 flex items-center gap-2"><Leaf size={16} /> The Store</p>
          <h1 className="font-serif text-4xl md:text-6xl text-bone mb-4">Natural & Earth Products</h1>
          <p className="text-bone/55 max-w-2xl text-lg">Purchase sacred goods directly, or connect with trusted partners on Amazon, eBay and Alibaba for natural and earth products.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.map((p) => (
          <div key={p.id} className="group relative border border-white/10 bg-surface hover:border-gold/40 transition-colors overflow-hidden flex flex-col" data-testid="product-card">
            <div className={`aspect-[4/3] bg-gradient-to-br ${p.image_color} flex items-center justify-center relative overflow-hidden`}>
              <img src={productImg(p.name)} alt={p.name} className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              {p.type === "referral" && (
                <span className="absolute top-3 right-3 text-xs uppercase tracking-widest bg-black/70 text-gold px-3 py-1 z-10">{p.store}</span>
              )}
            </div>
            <div className="p-6 flex flex-col flex-1">
              <p className="text-xs uppercase tracking-widest text-gold mb-2">{p.category}</p>
              <h3 className="font-serif text-xl text-bone mb-2">{p.name}</h3>
              <p className="text-bone/50 text-sm leading-relaxed mb-5">{p.description}</p>
              <div className="mt-auto flex items-center justify-between">
                {p.type === "own" ? (
                  <span className="flex flex-col">
                    <span className="font-serif text-2xl text-gold" data-testid="product-price">{formatPrice(p.price, region)}</span>
                    {region.currency !== "USD" && (
                      <span className="text-bone/40 text-[10px] leading-tight" data-testid="product-price-note">≈ ${p.price?.toFixed(2)} · charged in USD</span>
                    )}
                  </span>
                ) : (
                  <span className="text-bone/40 text-sm">via {p.store}</span>
                )}
                <button onClick={() => buy(p)} disabled={loadingId === p.id} data-testid="store-checkout-button"
                  className="flex items-center gap-2 bg-gold text-black px-5 py-2.5 font-medium hover:bg-gold2 transition-colors disabled:opacity-60">
                  {p.type === "own" ? (<><ShoppingCart size={16} /> {loadingId === p.id ? "..." : "Buy"}</>) : (<><ExternalLink size={16} /> Shop</>)}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </main>
  );
}
